/**
 * @file 声かけの手引き (`voice_guide_v1.yaml`) の parse。
 *
 * @what: YAML を build 時に取り込み (@rollup/plugin-yaml)、module の評価時に一度だけ検証して
 *   `voiceGuide` として公開する。
 * @why: 文言の追加を YAML だけで終わらせ、UI から YAML の形を隠す。参照は常にこの1か所を通す。
 *   録音の解析を始めたときも、判定の基準をこの表から引けるようにしておく。
 * @failure: 形式違反は VoiceGuideError を throw する (= 欠けた手引きを出さずに落とす)。
 */

import rawGuide from "./voice_guide_v1.yaml";

/** 全ての場面に共通する心がけ。 */
export interface VoiceGuidePrinciple {
  label: string;
  body: string;
}

/** 1つの場面であり、避けたい言い方と、その置きかえを持つ。 */
export interface VoiceGuideCase {
  id: string;
  label: string;
  /** 避けたい言い方・振る舞い。 */
  ng: string[];
  /** 置きかえ。 */
  ok: string[];
  /** なぜそうするのかを1〜3文で述べる。 */
  why: string;
  /** 見落とされやすい場面かどうか (画面で印を付ける)。 */
  overlooked: boolean;
}

/** 場面のまとまり (解いている最中・解き終わったあと など)。 */
export interface VoiceGuideGroup {
  id: string;
  label: string;
  cases: VoiceGuideCase[];
}

export interface VoiceGuide {
  version: number;
  principles: VoiceGuidePrinciple[];
  groups: VoiceGuideGroup[];
}

export class VoiceGuideError extends Error {}

function fail(message: string): never {
  throw new VoiceGuideError(`voice guide: ${message}`);
}

function stringList(value: unknown, field: string): string[] {
  if (!Array.isArray(value) || value.length === 0) fail(`${field} must be non-empty`);
  return value.map((v, i) => {
    if (typeof v !== "string" || v === "") fail(`${field}[${i}] must be a non-empty string`);
    return v;
  });
}

export function parseVoiceGuide(raw: unknown): VoiceGuide {
  if (typeof raw !== "object" || raw === null) fail("root must be an object");
  const root = raw as Record<string, unknown>;
  if (root.version !== 1) fail(`unsupported version: ${String(root.version)}`);

  if (!Array.isArray(root.principles) || root.principles.length === 0) {
    fail("principles must be non-empty");
  }
  const principles: VoiceGuidePrinciple[] = root.principles.map((p, i) => {
    const item = p as Record<string, unknown>;
    if (typeof item.label !== "string" || item.label === "") {
      fail(`principles[${i}].label is required`);
    }
    if (typeof item.body !== "string" || item.body === "") {
      fail(`principles[${i}].body is required`);
    }
    return { label: item.label, body: item.body };
  });

  if (!Array.isArray(root.groups) || root.groups.length === 0) fail("groups must be non-empty");
  // 場面の id は群をまたいで一意にする。※なぜなら画面が id を要素の key と URL の
  // 目印に使うため、重複すると別の場面が同じものとして畳まれるためである。
  const seenCaseIds = new Set<string>();
  const seenGroupIds = new Set<string>();
  const groups: VoiceGuideGroup[] = root.groups.map((g, gi) => {
    const group = g as Record<string, unknown>;
    if (typeof group.id !== "string" || group.id === "") fail(`groups[${gi}].id is required`);
    if (seenGroupIds.has(group.id)) fail(`duplicate group id: ${group.id}`);
    seenGroupIds.add(group.id);
    if (typeof group.label !== "string" || group.label === "") {
      fail(`groups[${gi}].label is required`);
    }
    if (!Array.isArray(group.cases) || group.cases.length === 0) {
      fail(`groups[${gi}] (${group.id}): cases must be non-empty`);
    }
    const cases: VoiceGuideCase[] = group.cases.map((c, ci) => {
      const where = `groups[${gi}].cases[${ci}]`;
      const item = c as Record<string, unknown>;
      if (typeof item.id !== "string" || item.id === "") fail(`${where}.id is required`);
      if (seenCaseIds.has(item.id)) fail(`duplicate case id: ${item.id}`);
      seenCaseIds.add(item.id);
      if (typeof item.label !== "string" || item.label === "") fail(`${where}.label is required`);
      if (typeof item.why !== "string" || item.why === "") fail(`${where}.why is required`);
      if (item.overlooked !== undefined && typeof item.overlooked !== "boolean") {
        fail(`${where}.overlooked must be a boolean`);
      }
      return {
        id: item.id,
        label: item.label,
        ng: stringList(item.ng, `${where}.ng`),
        ok: stringList(item.ok, `${where}.ok`),
        why: item.why,
        overlooked: item.overlooked === true,
      };
    });
    return { id: group.id, label: group.label, cases };
  });

  return { version: 1, principles, groups };
}

export const voiceGuide = parseVoiceGuide(rawGuide);
